import type { Scene, Story } from "../types/story";

/**
 * Fetches and lightly validates a story JSON file.
 *
 * Forward-compatibility is a hard requirement:
 *  - Unknown fields are tolerated (we never reject on extra properties).
 *  - A higher `version` than we know about MUST NOT break playback. We log a
 *    notice and render with the fields we understand.
 *
 * We only fail hard on structural problems that make playback impossible
 * (missing scenes array, no start scene, dangling branch/path targets).
 *
 * NOTE: This NEVER generates or fetches story content from an LLM. The story
 * bank is a static, pre-authored bundle published by Tella Story Studio behind
 * human review. The player only READS it — either from the site's own
 * `public/` in local dev, or from the immutable content CDN in production. Two
 * shapes, same contract:
 *
 *  - Local (`VITE_CONTENT_BASE` unset): `public/stories/<storyId>.json`, assets
 *    relative to the site root — the `gen:assets` dev flow, unchanged.
 *  - CDN (`VITE_CONTENT_BASE=https://cdn.…/content/`): the studio's roll-out
 *    layout. Fetch the catalog `index.json`, look up the story's versioned
 *    `story.json` path, fetch that, and resolve each asset's RELATIVE `src`
 *    against the versioned story.json URL — no path rewriting (see the studio's
 *    DEPLOY.md § "Cloudflare R2 + CDN").
 */

/** Highest schema version this build was written against. */
export const KNOWN_VERSION = 1;

/**
 * One published story in the CDN catalog (`index.json`). Byte-compatible with
 * Story Studio's `CatalogEntry`; forward-compatible (extra fields tolerated).
 * `json` is the content-root-relative path to this version's story.json.
 */
export interface CatalogEntry {
  storyId: string;
  version: number;
  json: string;
  title?: string;
  language?: string;
  readingLevel?: string;
  ageBand?: string;
  estimatedMinutes?: number;
  source?: string;
  child?: string;
  [key: string]: unknown;
}

interface Catalog {
  stories?: CatalogEntry[];
  [key: string]: unknown;
}

/** A loaded story plus the base URL its RELATIVE asset `src` values resolve against. */
export interface LoadedStory {
  story: Story;
  /** Prefix for the story's relative asset paths (ends with "/"). */
  assetBase: string;
}

/**
 * Content root for the published story bank. Resolution:
 *   1. `?content=<url>` — per-link override (handy for previewing a CDN bundle).
 *   2. `VITE_CONTENT_BASE` — build-time env (the production CDN base).
 *   3. "" — local `public/`, the dev default.
 * A non-empty base is normalized to a trailing slash.
 */
export function contentBase(): string {
  let base = "";
  try {
    base = new URLSearchParams(window.location.search).get("content") ?? "";
  } catch {
    /* no window (SSR/tests) → env only */
  }
  base = (base || (import.meta.env.VITE_CONTENT_BASE as string | undefined) || "").trim();
  if (!base) return "";
  return base.endsWith("/") ? base : `${base}/`;
}

/** Fetch + parse JSON; null on any non-OK/parse failure. Aborts still throw. */
async function fetchJsonOptional<T>(
  url: string,
  signal?: AbortSignal
): Promise<T | null> {
  try {
    const res = await fetch(url, { signal });
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") throw err;
    return null;
  }
}

/**
 * Fetch the published story catalog (`index.json`) — the whole shelf.
 *
 * Try the content base first when set (the production CDN), then same-origin (a
 * local `public/index.json`, an optional dev nicety so `npm run dev` can show a
 * shelf too). A missing or
 * unreachable catalog is NON-fatal — returns `[]`, and the caller falls back to
 * a `?story=` deep-link.
 *
 * This reads static, pre-authored metadata only; it NEVER generates content.
 */
export async function loadCatalog(
  signal?: AbortSignal
): Promise<CatalogEntry[]> {
  const base = contentBase();
  const local = `${import.meta.env.BASE_URL}index.json`;
  const candidates = base ? [`${base}index.json`, local] : [local];
  for (const url of candidates) {
    const catalog = await fetchJsonOptional<Catalog>(url, signal);
    if (catalog) return catalog.stories ?? [];
  }
  return [];
}

export async function loadStory(
  storyId: string,
  signal?: AbortSignal
): Promise<LoadedStory> {
  // Path traversal guard: storyId keys both a filename and a CDN prefix.
  if (!/^[A-Za-z0-9_-]+$/.test(storyId)) {
    throw new Error(`Invalid storyId: "${storyId}".`);
  }

  const base = contentBase();
  let storyUrl: string;
  let assetBase: string;

  if (base) {
    // CDN roll-out: catalog → versioned story.json → relative assets.
    const stories = await loadCatalog(signal);
    if (stories.length === 0) {
      throw new Error(`Could not load the story catalog from ${base}index.json.`);
    }
    const matches = stories.filter((e) => e?.storyId === storyId);
    if (matches.length === 0) {
      throw new Error(`Story "${storyId}" is not in the published catalog.`);
    }
    // One entry per storyId in practice; pick the highest version defensively.
    const entry = matches.reduce((a, b) => (b.version > a.version ? b : a));
    const jsonPath = String(entry.json);
    storyUrl = `${base}${jsonPath}`;
    // Assets resolve relative to the story.json URL, so the asset base is its
    // directory: content/<storyId>/v<n>/. (The studio writes asset keys under
    // this same version prefix, so the relative `src` lands with no rewriting.)
    assetBase = `${base}${jsonPath.slice(0, jsonPath.lastIndexOf("/") + 1)}`;
  } else {
    // Local dev: shipped file under public/, assets relative to the site root.
    storyUrl = `${import.meta.env.BASE_URL}stories/${storyId}.json`;
    assetBase = import.meta.env.BASE_URL;
  }

  const res = await fetch(storyUrl, { signal });
  if (!res.ok) {
    throw new Error(`Could not load story "${storyId}" (${res.status}).`);
  }

  const raw = (await res.json()) as Story;
  validateStory(raw);

  if (typeof raw.version === "number" && raw.version > KNOWN_VERSION) {
    // Forward-compatible: keep going, just note it.
    console.info(
      `[tella] story "${raw.storyId}" is version ${raw.version}; this build ` +
        `knows v${KNOWN_VERSION}. Rendering known fields only.`
    );
  }

  return { story: raw, assetBase };
}

/** Index scenes by id for O(1) lookup during playback. */
export function indexScenes(story: Story): Map<string, Scene> {
  const map = new Map<string, Scene>();
  for (const scene of story.scenes ?? []) {
    if (scene && typeof scene.id === "string") map.set(scene.id, scene);
  }
  return map;
}

/** Throws on structural problems that make the story unplayable. */
export function validateStory(story: Story): void {
  if (!story || typeof story !== "object") {
    throw new Error("Story file is not an object.");
  }
  if (!Array.isArray(story.scenes) || story.scenes.length === 0) {
    throw new Error("Story has no scenes.");
  }

  const ids = indexScenes(story);

  if (!story.startSceneId || !ids.has(story.startSceneId)) {
    throw new Error(
      `startSceneId "${story.startSceneId}" is missing from scenes.`
    );
  }

  // Pre-generated branches only: every choice target must already exist.
  for (const scene of story.scenes) {
    const opts = scene.choice?.options ?? [];
    for (const opt of opts) {
      if (!ids.has(opt.nextSceneId)) {
        throw new Error(
          `Scene "${scene.id}" choice points to unknown scene "${opt.nextSceneId}".`
        );
      }
    }
  }

  // canonicalPath (used by passive mode) must reference existing scenes.
  if (Array.isArray(story.canonicalPath)) {
    for (const sid of story.canonicalPath) {
      if (!ids.has(sid)) {
        throw new Error(`canonicalPath references unknown scene "${sid}".`);
      }
    }
  }
}
